import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import Config from '../utils/config.service';
import AngularHelperService from '../services/angular-helper.service';
import AngularBroadcastHelperService from '../services/angular-broadcast-helper.service';
import moment from 'moment';

@Injectable({ providedIn: 'root' })
export default class FetchingService {
	private httpClient: HttpClient = inject(HttpClient);
	private config: Config = inject(Config);
	private angularHelperService: AngularHelperService = inject(AngularHelperService);
	private angularBroadcastHelperService: AngularBroadcastHelperService = inject(AngularBroadcastHelperService);
	
	store: any = {};
	EXPIRY_THRESHOLD: any = 900; // In seconds
	baseUrls: any = undefined;
	FETCH_URLS: any = undefined;
	
	constructor() {
		this.baseUrls = {
			public: this.config.getBaseUrl(),
			rest: this.config.getRestUrl(),
		};
		
		this.FETCH_URLS = {
			'QUESTIONNAIRE': this.baseUrls.rest + 'publicrest/questionnaire',
			'CATEGORIES': this.baseUrls.rest + 'publicrest/business/service/fields/health',
			'THERAPY_TOPIC_TRANSLATIONS': this.baseUrls.rest + 'publicrest/business/therapy/translations',
			'COACHING_TOPICS': this.baseUrls.rest + 'rest/digital/resource/coaching/topics',
			'HSPS_EE_ET': this.baseUrls.public + '/data/ee-et/hsps',
			'HSPS_EE_EN': this.baseUrls.public + '/data/ee-en/hsps',
			'HSPS_EE_RU': this.baseUrls.public + '/data/ee-ru/hsps',
			'HSPS_FI_FI': this.baseUrls.public + '/data/fi-fi/hsps',
			'HSPS_FI_EN': this.baseUrls.public   + '/data/fi-en/hsps',
			'DESCRIPTIONS_ET': this.baseUrls.public + '/data/et/descriptions',
			'DESCRIPTIONS_EN': this.baseUrls.public + '/data/en/descriptions',
			'DESCRIPTIONS_RU': this.baseUrls.public + '/data/ru/descriptions',
			'DESCRIPTIONS_FI': this.baseUrls.public + '/data/fi/descriptions',
			'RISK_FACTORS': this.baseUrls.rest + 'rest/im/risk/factors/required',
			'RISK_FACTORS_MINORS': this.baseUrls.rest + 'rest/im/risk/factors/required',
			'RISK_FACTORS_PRE_FILL' : this.baseUrls.rest + 'rest/im/risk/factors/required/prefill/data',
			'SESSION': this.baseUrls.rest + 'rest/session/updated',
			'TESTIMONIALS_ET': this.baseUrls.public + '/data/et/testimonials',
			'TESTIMONIALS_EN': this.baseUrls.public + '/data/en/testimonials',
			'TESTIMONIALS_RU': this.baseUrls.public + '/data/ru/testimonials',
			'TESTIMONIALS_FI': this.baseUrls.public + '/data/fi/testimonials',
			'USER_WALLET': this.baseUrls.rest + 'rest/wallet',
		};
	}
	
	get(key, options?) {
		var deferred: any = this.angularHelperService.getDeferredPromise();
		if (!key || !this.FETCH_URLS[key]) {
			console.error('Fetching service failed to get data due to invalid key: ' + key + '. Make sure key is defined and listed in this.FETCH_URLS.');
			deferred.resolve();
			return deferred.promise;
		}
		
		options = options || {};
		options.params = options.params || {};
		options.refetch = options.refetch || false;
		options.threshold = options.threshold || this.EXPIRY_THRESHOLD;
		
		var url: any = this.constructUrl(key, options);
		this.store[url] = this.store[url] || {};
		
		if (this.shouldFetch(url, options)) {
			// console.log('FS.' + key + ': ' + 'fetching ' + url);
			this.fetch(url)
				.then((res) => {
					deferred.resolve(res);
				})
				.catch((err) => {
					deferred.reject(err);
				});
		} else {
			if (!this.store[url].isFetching && this.store[url].response) {
				//console.log('FS.' + key + ': ' + 'from cache ' + url);
				deferred.resolve(this.store[url].response);
			} else {
				//console.log('FS.' + key + ': ' + 'waiting ' + url);
				this.waitForResponse(url)
					.then((res) => {
						deferred.resolve(res);
					}).catch((err) => {
						deferred.reject(err);
					});
			}
		}
		
		return deferred.promise;
	}
	
	fetch(url) {
		var deferred: any = this.angularHelperService.getDeferredPromise();
		this.store[url] = { isFetching: true };
		
		this.httpClient.get(url, { withCredentials: true }).toPromise()
			.then((res) => {
				this.angularBroadcastHelperService.rootBroadcast('FS.' + url + '.fetching.success', res);
				this.store[url] = {
					isFetching: false,
					response: res,
					updatedAt: new Date()
				};
				deferred.resolve(res);
			})
			.catch((err) => {
				console.error('Fetching service failed to fetch from ' + url + ', error: ' + err);
				this.angularBroadcastHelperService.rootBroadcast('FS.' + url + '.fetching.error', err);
				this.store[url] = {};
				deferred.reject(err);
			});
		
		return deferred.promise;
	}
	
	waitForResponse(url) {
		var deferred: any = this.angularHelperService.getDeferredPromise();
		var listeners: any = {
			onSuccess: this.angularBroadcastHelperService.onRootEvent('FS.' + url + '.fetching.success', (e, data) => {
				this.unbindResponseListeners(listeners);
				deferred.resolve(data);
			}),
			onError: this.angularBroadcastHelperService.onRootEvent('FS.' + url + '.fetching.error', (e, err) => {
				this.unbindResponseListeners(listeners);
				deferred.reject(err);
			})
		};
		
		return deferred.promise;
	}
	
	unbindResponseListeners(listeners) {
		if (!listeners) return;
		var handlers: any = Object.keys(listeners);
		this.angularHelperService.forEach(handlers, (handler) => {
			if (listeners[handler] instanceof Function) {
				listeners[handler]();
			}
		});
	}
	
	shouldFetch(url, options) {
		if (!url) return false;
		options = options || {};
		options.threshold = options.threshold || 0;
		
		var cache: any = this.store[url] || {};
		var secondsSinceLastResponse: any = moment.duration(moment().diff(moment(cache.updatedAt))).asSeconds();
		var isExpired: any = cache.updatedAt ? secondsSinceLastResponse > options.threshold : false;
		
		return (!cache.isFetching && (!cache.response || isExpired)) || options.refetch;
	}
	
	constructUrl(key, options) {
		var url: any = this.FETCH_URLS[key];
		options = options || {};
		options.params = options.params || {};
		var params: any = Object.keys(options.params).map((item) => {
			return item + '=' + options.params[item];
		}).join('&');
		if (params.length) url += '?' + params;
		
		return url;
	}
	
	clear(key) {
		var url: any = this.FETCH_URLS[key];
		delete this.store[url];
	}
	
}
